import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ss from "../../assets/images/SS.png";
import contact from "../../assets/images/contact.png";
import "../../styles/DbUpdateManager.css";

function DbUpdateManager() {
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [logs, setLogs] = useState([]);
  const [errors, setErrors] = useState([]);
  const [progress, setProgress] = useState(0);
  const [activeTab, setActiveTab] = useState("logs");
  const [changelog, setChangelog] = useState([]);
  const [showChangelog, setShowChangelog] = useState(null);

  // Script Management State
  const [categories, setCategories] = useState([]);
  const [enabledCategories, setEnabledCategories] = useState({}); 
  const [selectedVersions, setSelectedVersions] = useState({}); 
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeFile, setActiveFile] = useState(null);
  const [editedCode, setEditedCode] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Custom Query State
  const [customQuery, setCustomQuery] = useState("");
  const [isExecutingCustom, setIsExecutingCustom] = useState(false);
  const [runningScript, setRunningScript] = useState(null);
  const [completedScripts, setCompletedScripts] = useState([]);

  const logEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCompanies();
    fetchScripts();
  }, []);

  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/dbupdate/companies`);
      setCompanies(response.data);
    } catch (error) {
      addLog("error", "Failed to fetch company list.");
    }
  };

  const fetchScripts = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/dbupdate/scripts`);
      setCategories(response.data);
      
      const initialEnabled = {};
      const initialVersions = {};
      response.data.forEach(cat => {
        initialEnabled[cat.key] = true;
        if (cat.files && cat.files.length > 0) {
          initialVersions[cat.key] = cat.files[0];
        }
      });
      setEnabledCategories(prev => ({ ...initialEnabled, ...prev }));
      setSelectedVersions(prev => ({ ...initialVersions, ...prev }));

      if (response.data.length > 0) {
        const cat = response.data[0];
        setActiveCategory(cat);
        if (cat.files && cat.files.length > 0) {
          handleSelectFileForEdit(cat, cat.files[0]);
        }
      }
    } catch (error) {
      addLog("error", "Failed to load SQL scripts.");
    }
  };

  const handleSelectFileForEdit = async (category, fileName) => {
    setActiveCategory(category);
    setActiveFile(fileName);
    try {
      const folderParam = category.folder || "";
      const res = await axios.get(`${apiBaseUrl}/dbupdate/script-content?folder=${folderParam}&file=${fileName}`);
      setEditedCode(res.data.content);
    } catch (e) {
      setEditedCode("-- Error loading content --");
    }
  };

  const addLog = (type, message) => {
    setLogs((prev) => [
      ...prev,
      { type, message, time: new Date().toLocaleTimeString() },
    ]);
  };

  const handleSelectCompany = (dbCode) => {
    setSelectedCompanies((prev) =>
      prev.includes(dbCode)
        ? prev.filter((c) => c !== dbCode)
        : [...prev, dbCode]
    );
  };

  const handleSelectAll = () => {
    if (selectedCompanies.length === companies.length) {
      setSelectedCompanies([]);
    } else {
      setSelectedCompanies(companies.map((c) => c.DbName));
    }
  };

  const toggleCategory = (key) => {
    setEnabledCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const changeVersion = (key, file) => {
    setSelectedVersions(prev => ({ ...prev, [key]: file }));
  };

  const fetchChangelog = async (dbCode) => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/dbupdate/changelog/${dbCode}`
      );
      setChangelog(response.data);
      setShowChangelog(dbCode);
      setActiveTab("changelog");
    } catch (error) {
      addLog("error", `Failed to fetch changelog for ${dbCode}`);
    }
  };

  const handleSaveScript = async () => {
    if (!activeCategory || !activeFile || isSaving) return;
    const confirmSave = window.confirm(`Save changes to ${activeFile}?`);
    if (!confirmSave) return;

    setIsSaving(true);
    try {
      await axios.post(`${apiBaseUrl}/dbupdate/scripts/save`, {
        folder: activeCategory.folder,
        file: activeFile,
        content: editedCode,
      });
      addLog("success", `Updated ${activeFile}`);
      alert("Saved successfully!");
    } catch (error) {
      alert("Save failed: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !activeCategory) return;

    const confirmUpload = window.confirm(`Are you sure you want to upload "${file.name}" to the "${activeCategory.label}" folder?`);
    if (!confirmUpload) {
      event.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("scriptFile", file);

    setIsUploading(true);
    try {
      await axios.post(`${apiBaseUrl}/dbupdate/scripts/upload?category=${activeCategory.key}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      addLog("success", `Uploaded new version to ${activeCategory.label}`);
      fetchScripts();
      alert(`Uploaded! It will be saved with a timestamp.`);
    } catch (error) {
      alert("Upload failed: " + error.message);
    } finally {
      setIsUploading(false);
      event.target.value = ""; 
    }
  };

  const handleDeleteScript = async () => {
    if (!activeCategory || !activeFile) return;
    const confirmDelete = window.confirm(`Are you sure you want to PERMANENTLY delete ${activeFile}? This action cannot be undone.`);
    if (!confirmDelete) return;

    try {
      const folderParam = activeCategory.folder || "";
      await axios.delete(`${apiBaseUrl}/dbupdate/script?folder=${folderParam}&file=${activeFile}`);
      addLog("success", `Deleted script: ${activeFile}`);
      
      // Refresh scripts list and reset selection
      fetchScripts();
      setActiveFile(null);
      setEditedCode("");
    } catch (error) {
      alert("Delete failed: " + error.message);
    }
  };

  const runUpdate = async () => {
    if (selectedCompanies.length === 0 || isUpdating) return;

    const pipeline = [];
    categories.forEach(cat => {
        if (enabledCategories[cat.key] && (selectedVersions[cat.key] || cat.file)) {
            pipeline.push({
                folder: cat.folder || "",
                file: selectedVersions[cat.key] || cat.file
            });
        }
    });

    if (pipeline.length === 0) {
        alert("Please select at least one script to run.");
        return;
    }

    setIsUpdating(true);
    setLogs([]);
    setErrors([]);
    setProgress(0);
    setRunningScript(null);
    setCompletedScripts([]);
    setActiveTab("logs");

    await executeSsePipeline(`${apiBaseUrl}/dbupdate/run`, { dbCodes: selectedCompanies, pipeline });
  };

  const runCustomQuery = async () => {
    if (selectedCompanies.length === 0 || isExecutingCustom || !customQuery.trim()) return;

    const upper = customQuery.toUpperCase();
    const isDangerous = (upper.includes("DELETE") || upper.includes("UPDATE")) && !upper.includes("WHERE");
    const isDrop = upper.includes("DROP");

    if (isDrop) {
      const confirmDrop = window.confirm("⚠️ WARNING: This query contains a DROP statement. This will PERMANENTLY delete database objects. Are you ABSOLUTELY sure?");
      if (!confirmDrop) return;
    } else if (isDangerous) {
      const confirmDanger = window.confirm("⚠️ WARNING: This query contains a DELETE or UPDATE statement without a WHERE condition. This will affect ALL records in the target table(s). Are you sure you want to proceed?");
      if (!confirmDanger) return;
    } else {
      const confirmAction = window.confirm(`Are you sure you want to execute this query on ${selectedCompanies.length} database(s)?`);
      if (!confirmAction) return;
    }

    setIsExecutingCustom(true);
    setLogs([]);
    setErrors([]);
    setProgress(0);
    setRunningScript(null);
    setActiveTab("logs");

    await executeSsePipeline(`${apiBaseUrl}/dbupdate/run-custom`, { dbCodes: selectedCompanies, sqlQuery: customQuery });
    setIsExecutingCustom(false);
  };

  const executeSsePipeline = async (url, body) => {
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        let buffer = "";
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop(); // Keep the potentially incomplete last part in buffer

            parts.forEach(line => {
                if (line.startsWith('data: ')) {
                    try {
                        const data = JSON.parse(line.substring(6));
                        if (data.type === "start") {
                            addLog("info", data.message);
                        } else if (data.type === "db_start") {
                            addLog("info", data.message);
                        } else if (data.type === "script_start") {
                            addLog("info", data.message);
                            setRunningScript(data.script);
                        } else if (data.type === "db_done" || data.type === "script_done") {
                            addLog("success", data.message);
                            if (data.progress !== undefined) setProgress(data.progress);
                        } else if (data.type === "db_error" || data.type === "script_error") {
                            addLog("error", data.message);
                            if (data.progress !== undefined) setProgress(data.progress);
                            setErrors(prev => [...prev, { db: data.dbCode, script: data.script || 'Custom Query', message: data.message, output: data.output }]);
                        } else if (data.type === "script_all_done") {
                            addLog("success", data.message);
                            setCompletedScripts(prev => [...prev, data.script]);
                        } else if (data.type === "complete") {
                            addLog("success", data.message);
                            setProgress(100);
                            setIsUpdating(false);
                            setRunningScript(null);
                        }
                    } catch (e) {
                        console.error("Failed to parse SSE line:", line, e);
                    }
                }
            });
        }
    } catch (err) {
        addLog("error", "Execution failed: " + err.message);
        setIsUpdating(false);
        setRunningScript(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-[#05070a] text-gray-300 font-sans flex flex-col h-screen w-screen selection:bg-indigo-500/30">
      {/* Premium Header */}
      <header className="flex-none flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#1a1f2e] to-[#0a0c10] border-b border-white/5 shadow-2xl">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20 text-white font-bold text-lg">
              DB
            </div>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">SherGold DB Update Manager</h1>
              <p className="text-xs text-indigo-400 font-medium opacity-80 uppercase tracking-widest mt-0.5">Versioned Database Sync Engine</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button 
              className="px-4 h-10 flex items-center justify-center rounded-xl bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 hover:text-white hover:bg-indigo-600 transition-all font-semibold text-sm"
              onClick={() => navigate("/checktables-generator")}
            >
              Schema Generator
            </button>
            <button 
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:text-white hover:bg-red-500 transition-all font-bold"
              onClick={() => navigate("/")}
            >
              	&#x2715;
            </button>
          </div>
        </header>

        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Left Sidebar: Controls & Pipeline */}
          <aside className="w-[350px] flex-none flex flex-col bg-[#0d1018] border-r border-white/5 min-h-0">
            <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
              {/* Database Selection Card */}
              <div className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden shadow-xl">
                <div className="px-4 py-3 bg-white/[0.02] border-b border-white/5 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Client Databases</span>
                  <span className="bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded-full text-[10px] font-bold border border-indigo-500/20">{companies.length}</span>
                </div>
                
                <div className="p-2 bg-black/40 border-b border-white/5 flex items-center justify-between">
                  <label className="flex items-center gap-2 px-2 py-1 cursor-pointer group">
                    <input type="checkbox" className="w-4 h-4 rounded border-gray-600 bg-black/20 text-indigo-600 outline-none" checked={selectedCompanies.length === companies.length && companies.length > 0} onChange={handleSelectAll} />
                    <span className="text-xs font-semibold text-gray-400 group-hover:text-gray-200 transition-colors">Select All</span>
                  </label>
                  <span className="text-[10px] font-bold text-indigo-500/60 pr-2">{selectedCompanies.length} selected</span>
                </div>

                <div className="max-h-[250px] overflow-y-auto divide-y divide-white/5 scroll-py-1 custom-scrollbar">
                  {companies.map((company) => (
                    <div 
                      key={company.DbName} 
                      className={`group flex items-center gap-3 p-3 cursor-pointer transition-all hover:bg-indigo-500/5 ${selectedCompanies.includes(company.DbName) ? 'bg-indigo-500/[0.07]' : ''}`}
                      onClick={() => handleSelectCompany(company.DbName)}
                    >
                      <input type="checkbox" className="w-4 h-4 rounded border-gray-600 bg-black/40 text-indigo-600 transition-all group-hover:border-indigo-500/50" checked={selectedCompanies.includes(company.DbName)} readOnly />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold text-gray-200 truncate group-hover:text-white transition-colors">{company.Name}</div>
                        <div className="text-[10px] text-gray-500 font-mono mt-0.5">{company.DbName}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Execution Pipeline Card */}
              <div className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden shadow-xl">
                <div className="px-4 py-3 bg-white/[0.02] border-b border-white/5 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">Execution Pipeline</span>
                  <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full text-[10px] font-bold border border-emerald-500/20">{categories.filter(c => enabledCategories[c.key]).length} active</span>
                </div>
                
                <div className="p-3 space-y-3">
                  {categories.map((cat, idx) => (
                    <div 
                      key={cat.key} 
                      className={`flex flex-col gap-2 p-3 rounded-xl border transition-all ${enabledCategories[cat.key] ? 'bg-indigo-500/[0.03] border-indigo-500/30 shadow-inner' : 'bg-black/20 border-white/5 opacity-40 grayscale'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <input type="checkbox" className="w-4 h-4 rounded-full border-gray-600 text-indigo-600" checked={enabledCategories[cat.key] || false} onChange={() => toggleCategory(cat.key)} />
                          <span className="text-xl leading-none">{cat.icon}</span>
                          <span className="text-xs font-bold text-gray-200 tracking-tight">{cat.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {completedScripts.includes(selectedVersions[cat.key] || cat.file) ? (
                            <span className="text-[10px] font-bold text-emerald-500 flex items-center gap-1">
                              <span className="text-sm">✔</span> Done
                            </span>
                          ) : (isUpdating && (selectedVersions[cat.key] === runningScript || cat.file === runningScript)) ? (
                            <span className="flex h-2 w-2 relative">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                            </span>
                          ) : enabledCategories[cat.key] && (
                            <span className="text-[8px] font-black text-white/20 uppercase tracking-tighter">Pending</span>
                          )}
                          <span className="text-[10px] font-black text-white/20 select-none">0{idx + 1}</span>
                        </div>
                      </div>
                      
                      {enabledCategories[cat.key] && (
                        <div className="relative">
                          <select 
                            className="w-full pl-3 pr-8 py-1.5 bg-black/60 border border-white/10 text-[10px] text-indigo-300 rounded-lg outline-none appearance-none cursor-pointer font-mono"
                            value={selectedVersions[cat.key] || ''}
                            onChange={(e) => changeVersion(cat.key, e.target.value)}
                          >
                            {cat.files.map(f => <option key={f} value={f}>{f}</option>)}
                          </select>
                          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[10px] text-indigo-500 opacity-50 font-bold select-none">▼</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Action Footer */}
            <div className="flex-none p-4 bg-black/40 border-t border-white/10">
              <button 
                className={`w-full py-4 rounded-2xl flex items-center justify-center gap-3 font-bold text-sm tracking-wide shadow-2xl transition-all duration-300 transform active:scale-95 ${selectedCompanies.length === 0 || isUpdating ? 'bg-gray-700 text-gray-400 grayscale opacity-50 cursor-not-allowed shadow-none' : 'bg-emerald-600 text-white hover:bg-emerald-500 shadow-emerald-500/20 active:bg-emerald-700'}`}
                onClick={runUpdate} 
                disabled={selectedCompanies.length === 0 || isUpdating}
              >
                {isUpdating ? (
                  <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> <span className="text-white">Syncing Data...</span></>
                ) : (
                  <><span>Run Selected Update</span> <span className="text-xl translate-y-px text-white/90">⚡</span></>
                )}
              </button>
            </div>
          </aside>

        {/* Main Content: Registry, Logs, Runner */}
        <main className="flex-1 flex flex-col bg-[#05070a] min-w-0">
          {/* Global Progress Section */}
          <div className="px-8 py-6 bg-gradient-to-b from-white/[0.02] to-transparent border-b border-white/5 flex-none overflow-hidden">
             <div className="flex justify-between items-end mb-3">
               <div>
                 <span className="text-xs font-bold text-indigo-400 uppercase tracking-[0.2em] block mb-1">Global Synchronization</span>
                 <h2 className="text-sm font-medium text-gray-400 italic">Processing scripts across {selectedCompanies.length} clusters...</h2>
               </div>
               <div className="text-right">
                 <span className="text-3xl font-black text-indigo-500 leading-none">{Math.round(progress)}<span className="text-sm opacity-50">%</span></span>
               </div>
             </div>
             <div className="h-3 bg-black/60 rounded-full overflow-hidden border border-white/5 shadow-inner p-0.5">
               <div 
                 className={`h-full bg-gradient-to-r from-indigo-600 via-purple-500 to-emerald-500 rounded-full transition-all duration-500 relative ${isUpdating || isExecutingCustom ? 'animate-pulse' : ''}`}
                 style={{ width: `${progress}%` }}
               >
                 {(isUpdating || isExecutingCustom) && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-[shimmer_2s_infinite] -skew-x-12" />}
               </div>
             </div>
          </div>

          {/* Persistent Tab System */}
          <div className="flex-1 flex flex-col min-h-0">
            <nav className="flex px-6 pt-4 gap-2 flex-none border-b border-white/5">
              {[
                { id: 'logs', label: 'Realtime Logs', icon: '📝' },
                { id: 'scripts', label: 'Script Registry', icon: '📜' },
                { id: 'query', label: 'Query Runner', icon: '⚡' },
                { id: 'errors', label: `Error Console (${errors.length})`, icon: '🛑', color: errors.length > 0 ? 'text-red-400' : 'text-gray-500' }
              ].map(tab => (
                <button 
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 text-xs font-bold rounded-t-xl transition-all flex items-center gap-3 border-x border-t relative ${activeTab === tab.id ? 'bg-[#0a0c10] border-white/10 text-white shadow-[-10px_-10px_20px_rgba(0,0,0,0.5)]' : 'bg-transparent border-transparent text-gray-500 hover:text-gray-300'}`}
                >
                  <span>{tab.icon}</span>
                  <span className={tab.color}>{tab.label}</span>
                  {tab.id === 'errors' && errors.length > 0 && <span className="absolute -top-1 -right-1 flex h-4 w-4"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-[10px] items-center justify-center text-white">{errors.length}</span></span>}
                </button>
              ))}
            </nav>

            <div className="flex-1 overflow-hidden p-6 bg-[#0a0c10]">
              {/* Logs View */}
              {activeTab === "logs" && (
                <div className="h-full overflow-y-auto font-mono text-xs space-y-1 custom-scrollbar pr-4">
                  {logs.length === 0 && <div className="h-full flex flex-col items-center justify-center opacity-10 text-center"><span className="text-6xl mb-4">🛰️</span><span className="italic">Awaiting satellite command link...</span></div>}
                  {logs.map((log, idx) => (
                    <div key={idx} className={`flex gap-4 p-2 rounded-lg border transition-all ${log.type === 'error' ? 'bg-red-500/5 border-red-500/10 text-red-300' : log.type === 'success' ? 'bg-emerald-500/5 border-emerald-500/10 text-emerald-300' : 'bg-indigo-500/[0.02] border-indigo-500/10 text-indigo-300/80 hover:bg-white/5'}`}>
                      <span className="flex-none opacity-40 font-black whitespace-nowrap">{idx + 1}</span>
                      <span className="flex-none w-4 text-center">{log.type === "info" ? "ℹ" : log.type === "success" ? "✔" : "❌"}</span>
                      <span className="flex-1 whitespace-pre-wrap leading-relaxed">{log.message}</span>
                      <span className="flex-none text-[10px] opacity-20 self-center uppercase tracking-widest">{log.time}</span>
                    </div>
                  ))}
                  <div ref={logEndRef} />
                </div>
              )}

              {/* Script Registry View */}
              {activeTab === "scripts" && (
                <div className="h-full flex flex-col rounded-2xl overflow-hidden border border-white/5 shadow-2xl bg-[#030509]">
                  <header className="flex flex-wrap items-center gap-4 p-4 bg-white/[0.03] border-b border-white/5">
                    <div className="flex items-center gap-4 overflow-x-auto pb-1 flex-1 custom-scrollbar-hide">
                      {categories.map(cat => (
                        <button 
                          key={cat.key} 
                          className={`px-4 py-2 text-[10px] font-bold rounded-xl whitespace-nowrap transition-all border ${activeCategory?.key === cat.key ? 'bg-indigo-600 text-white border-indigo-500 shadow-lg shadow-indigo-600/20' : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'}`}
                          onClick={() => {
                            setActiveCategory(cat);
                            if (cat.files && cat.files.length > 0) handleSelectFileForEdit(cat, cat.files[0]);
                            else if (cat.file) handleSelectFileForEdit(cat, cat.file);
                          }}
                        >
                          {cat.icon} {cat.label}
                        </button>
                      ))}
                    </div>

                    <div className="flex items-center gap-3">
                      {activeCategory && (activeCategory.files?.length > 0 || activeCategory.file) && (
                          <select 
                            className="bg-black/80 border border-white/10 text-xs text-indigo-300 rounded-xl px-4 py-2 outline-none cursor-pointer appearance-none px-6"
                          value={activeFile || ''} 
                          onChange={(e) => handleSelectFileForEdit(activeCategory, e.target.value)}
                        >
                          {activeCategory.files ? activeCategory.files.map(f => <option key={f} value={f}>{f}</option>) : <option value={activeCategory.file}>{activeCategory.file}</option>}
                        </select>
                      )}
                      
                      <div className="h-6 w-px bg-white/10 mx-1" />

                      <button onClick={() => fileInputRef.current.click()} className="px-4 py-2 text-[10px] font-black bg-indigo-500/10 text-indigo-400 rounded-xl border border-indigo-500/20 hover:bg-indigo-500/20 transition-all flex items-center gap-2" disabled={isUploading}>
                        {isUploading ? "Uploading..." : <><span className="text-sm">+</span> New Version</>}
                      </button>
                      <input type="file" ref={fileInputRef} className="hidden" accept=".sql" onChange={handleFileUpload} />

                      <button onClick={handleDeleteScript} disabled={!activeFile || isSaving} className="px-4 py-2 text-[10px] font-black bg-red-500/10 text-red-500 rounded-xl border border-red-500/20 hover:bg-red-500/20 transition-all">🗑️ Delete</button>
                      
                      <button onClick={handleSaveScript} disabled={isSaving || !activeFile} className="px-6 py-2 text-[10px] font-black bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl shadow-lg shadow-emerald-600/20 transition-all active:scale-95 disabled:opacity-20 text-center flex items-center justify-center">
                        {isSaving ? "Saving..." : "💾 Save"}
                      </button>
                    </div>
                  </header>

                  <div className="flex-1 bg-black/40 relative">
                      <textarea 
                        className="absolute inset-0 w-full h-full p-8 !bg-transparent text-indigo-200/90 font-mono text-sm leading-relaxed resize-none outline-none placeholder:opacity-5 overflow-y-auto custom-scrollbar" 
                      value={editedCode} 
                      onChange={(e) => setEditedCode(e.target.value)} 
                      placeholder="Select a script to edit..."
                      spellCheck="false" 
                    />
                  </div>
                </div>
              )}

              {activeTab === "query" && (
                <div className="h-full flex flex-col rounded-2xl overflow-hidden border border-white/5 bg-[#030509]">
                   <div className="p-4 bg-orange-500/5 border-b border-orange-500/10 flex items-center justify-between">
                     <div className="flex flex-col">
                       <span className="text-[10px] font-black text-orange-500 tracking-[0.3em] uppercase mb-1">Advanced Execution</span>
                       <span className="text-[11px] text-orange-200/50 italic opacity-80">Requires elevated administrative clearance. High-risk cross-DB operations.</span>
                     </div>
                     <button 
                       onClick={runCustomQuery} 
                       disabled={isExecutingCustom || !customQuery.trim() || selectedCompanies.length === 0}
                       className="px-8 py-3 text-xs font-black bg-orange-600 hover:bg-orange-500 text-white rounded-xl shadow-xl shadow-orange-900/40 transition-all transform active:scale-95 disabled:bg-gray-700 disabled:text-gray-400 disabled:opacity-50 flex items-center justify-center"
                     >
                       {isExecutingCustom ? "EXECUTING..." : "FLASH EXECUTE ⚡"}
                     </button>
                   </div>
                   <div className="flex-1 relative">
                        <textarea 
                          className="absolute inset-0 w-full h-full p-10 bg-transparent text-orange-100/90 font-mono text-base leading-relaxed resize-none outline-none placeholder:text-white/5 selection:bg-orange-500/40 custom-scrollbar" 
                        value={customQuery} 
                        onChange={(e) => setCustomQuery(e.target.value)} 
                        placeholder="ENTER UNIVERSAL SQL COMMAND..."
                        spellCheck="false" 
                      />
                   </div>
                </div>
              )}

              {activeTab === "errors" && (
                <div className="h-full overflow-y-auto pr-4 custom-scrollbar">
                  {errors.length === 0 && <div className="h-full flex flex-col items-center justify-center opacity-10 text-center"><span className="text-6xl mb-4">💎</span><span className="italic">Crystal clear system. No anomalies detected.</span></div>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6">
                    {errors.map((err, idx) => (
                      <div key={idx} className="bg-red-500/[0.03] border border-red-500/10 rounded-2xl overflow-hidden shadow-2xl">
                        <div className="px-6 py-4 bg-red-500/5 border-b border-red-500/10 flex items-center justify-between">
                          <span className="text-[10px] font-black text-red-400 tracking-widest uppercase truncate max-w-[150px]">{err.db}</span>
                          <span className="text-[10px] text-white/20 font-mono">{err.script}</span>
                        </div>
                        <div className="p-6">
                          <p className="text-sm font-bold text-red-200 leading-snug mb-4">{err.message}</p>
                          {err.output && <div className="bg-black/60 rounded-xl p-4 border border-white/5 font-mono text-[10px] text-red-300 leading-relaxed max-h-[200px] overflow-y-auto custom-scrollbar-mini">{err.output}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default DbUpdateManager;
